export type OfferDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type OfferV1 = {
  id: string;
  title: string;
  description?: string;
  discount?: string;
  code?: string;
  bgColor?: string;
  expiresAt?: string;
};

export type OfferV2 = {
  id: string;
  title: string;
  description?: string;
  discount?: string;
  code?: string;
  bgColor?: string;
  enabled?: boolean;
  link?: string;
  startsOn?: string; // YYYY-MM-DD in business timezone
  endsOn?: string; // YYYY-MM-DD in business timezone
  daysOfWeek?: OfferDayOfWeek[]; // 0=Sun ... 6=Sat (business timezone)
  dailyStartTime?: string; // HH:mm (business timezone)
  dailyEndTime?: string; // HH:mm (business timezone)
};

export type Offer = OfferV2;

const WEEKDAY_INDEX: Record<string, OfferDayOfWeek> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function toZonedParts(date: Date, timeZone?: string) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
    const year = get('year');
    const month = get('month');
    const day = get('day');
    const weekday = get('weekday');
    const hour = get('hour');
    const minute = get('minute');

    if (!year || !month || !day || !weekday || hour == null || minute == null) return null;
    return { year, month, day, weekday, hour, minute };
  } catch {
    return null;
  }
}

export function getZonedNowSnapshot(now: Date, timeZone?: string) {
  const zoned = toZonedParts(now, timeZone);
  if (zoned) {
    const ymd = `${zoned.year}-${zoned.month}-${zoned.day}`;
    const weekdayIndex = WEEKDAY_INDEX[zoned.weekday] ?? (now.getDay() as OfferDayOfWeek);
    const minutes = parseInt(zoned.hour, 10) * 60 + parseInt(zoned.minute, 10);
    return { ymd, weekdayIndex, minutes };
  }

  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const ymd = `${y}-${m}-${d}`;
  const weekdayIndex = now.getDay() as OfferDayOfWeek;
  const minutes = now.getHours() * 60 + now.getMinutes();
  return { ymd, weekdayIndex, minutes };
}

export function parseOffers(raw: unknown): Offer[] {
  if (typeof raw === 'string') {
    try {
      return parseOffers(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];

  const offers: Offer[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const anyItem = item as Partial<OfferV2 & OfferV1>;
    if (!anyItem.id || !anyItem.title) continue;

    offers.push({
      id: String(anyItem.id),
      title: String(anyItem.title),
      description: anyItem.description ? String(anyItem.description) : undefined,
      discount: anyItem.discount ? String(anyItem.discount) : undefined,
      code: anyItem.code ? String(anyItem.code) : undefined,
      bgColor: anyItem.bgColor ? String(anyItem.bgColor) : undefined,
      enabled: anyItem.enabled ?? true,
      link: anyItem.link ? String(anyItem.link) : undefined,
      startsOn: anyItem.startsOn ? String(anyItem.startsOn) : undefined,
      endsOn: anyItem.endsOn
        ? String(anyItem.endsOn)
        : anyItem.expiresAt
          ? String(anyItem.expiresAt)
          : undefined,
      daysOfWeek: Array.isArray(anyItem.daysOfWeek)
        ? (anyItem.daysOfWeek
            .map((d) => Number(d))
            .filter((d) => Number.isFinite(d) && d >= 0 && d <= 6) as OfferDayOfWeek[])
        : undefined,
      dailyStartTime: anyItem.dailyStartTime ? String(anyItem.dailyStartTime) : undefined,
      dailyEndTime: anyItem.dailyEndTime ? String(anyItem.dailyEndTime) : undefined,
    });
  }

  return offers;
}

export function serializeOffers(offers: Offer[]): string {
  const clean = offers.map((o) => ({
    id: o.id,
    title: o.title,
    description: o.description || '',
    discount: o.discount || '',
    code: o.code || '',
    bgColor: o.bgColor || '',
    enabled: o.enabled !== false,
    link: o.link || '',
    startsOn: o.startsOn || '',
    endsOn: o.endsOn || '',
    daysOfWeek: o.daysOfWeek && o.daysOfWeek.length ? o.daysOfWeek : [],
    dailyStartTime: o.dailyStartTime || '',
    dailyEndTime: o.dailyEndTime || '',
  }));
  return JSON.stringify(clean);
}

function parseTimeToMinutes(value: string | undefined) {
  if (!value) return null;
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

export function isOfferActive(offer: Offer, now: Date, timeZone?: string) {
  if (offer.enabled === false) return false;

  const snap = getZonedNowSnapshot(now, timeZone);

  if (offer.startsOn && snap.ymd < offer.startsOn) return false;
  if (offer.endsOn && snap.ymd > offer.endsOn) return false;

  if (offer.daysOfWeek && offer.daysOfWeek.length) {
    if (!offer.daysOfWeek.includes(snap.weekdayIndex)) return false;
  }

  const startMinutes = parseTimeToMinutes(offer.dailyStartTime);
  const endMinutes = parseTimeToMinutes(offer.dailyEndTime);
  if (startMinutes != null || endMinutes != null) {
    const start = startMinutes ?? 0;
    const end = endMinutes ?? 24 * 60;
    if (end >= start) {
      if (snap.minutes < start || snap.minutes > end) return false;
    } else {
      // overnight window, e.g. 20:00 -> 02:00
      if (!(snap.minutes >= start || snap.minutes <= end)) return false;
    }
  }

  return true;
}

export function getActiveOffers(offers: Offer[], now: Date, timeZone?: string) {
  return offers.filter((o) => isOfferActive(o, now, timeZone));
}

